import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { fetchNlGap } from '../services/analyticsService';
import { novaGenerateReport, novaDownloadReport } from '../services/novaService';
import { NovaQuickAnalysisBar } from '../components/shared/NovaQuickAnalysis';
import ReportLanguageToggle, { useReportLanguage } from '../components/shared/ReportLanguageToggle';
import './Suppliers.css';

const CHART_CARD = {
  background: 'white',
  borderRadius: 14,
  border: '1px solid #e3e8ef',
  padding: '14px 18px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 10,
};

const ATC_AREA_MAP = {
  A: 'Alimentary tract', B: 'Blood', C: 'Cardiovascular', D: 'Dermatologicals',
  G: 'Genito-urinary', H: 'Hormones', J: 'Anti-infectives', L: 'Antineoplastic',
  M: 'Musculo-skeletal', N: 'Nervous system', P: 'Antiparasitic', R: 'Respiratory',
  S: 'Sensory organs', V: 'Various',
};
const GAP_CHART_COLORS = ['#0D5C4F','#0B3D6B','#3B1F6E','#8B4513','#0F6B5B','#1466A7','#5C318C','#AB611F','#18A78F','#1E8FE0','#7D43AA','#E49937'];

const REG_FILTERS = [
  { key: '', label: 'All' },
  { key: 'ema', label: 'EMA' },
  { key: 'ansm', label: 'ANSM' },
];

export default function NLMarketGapAnalysis() {
  const { instance: msalInstance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState(() => {
    const p = new URLSearchParams(location.search);
    return p.get('inn') || '';
  });
  const [regFilter, setRegFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [sort, setSort] = useState({ col: 'inn', dir: 'asc' });
  const [exportingPptx, setExportingPptx] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportErr, setExportErr] = useState('');
  const { lang: reportLang, setLang: setReportLang } = useReportLanguage();

  // Sync URL ↔ molecule search
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (search.trim()) {
      if (p.get('inn') !== search.trim()) navigate(`?inn=${encodeURIComponent(search.trim())}`, { replace: true });
    } else if (p.has('inn')) {
      navigate(location.pathname, { replace: true });
    }
  }, [search, location.search, location.pathname, navigate]);

  const scopeToMolecule = useCallback((inn) => {
    setSearch(inn);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setErr('');
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (regFilter) params.register = regFilter;
    fetchNlGap(msalInstance, params)
      .then(d => setData(d))
      .catch(() => setErr('Failed to load NL gap data'))
      .finally(() => setLoading(false));
  }, [search, regFilter, msalInstance]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const gaps = useMemo(() => {
    if (!data?.gaps) return [];
    const arr = [...data.gaps];
    arr.sort((a, b) => {
      let av = a[sort.col], bv = b[sort.col];
      if (sort.col === 'suppliersCouldFill') { av = (a.suppliersCouldFill || []).length; bv = (b.suppliersCouldFill || []).length; }
      if (sort.col === 'dosageForms') { av = (a.dosageForms || []).length; bv = (b.dosageForms || []).length; }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sort.dir === 'desc' ? bv - av : av - bv;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sort.dir === 'desc' ? -cmp : cmp;
    });
    return arr;
  }, [data, sort]);

  const atcChartData = useMemo(() => {
    if (!gaps || gaps.length === 0) return [];
    const counts = new Map();
    for (const g of gaps) {
      const code = (g.atcCode || '').trim();
      const letter = code.length > 0 ? code[0].toUpperCase() : '';
      const label = letter && ATC_AREA_MAP[letter] ? `${letter} - ${ATC_AREA_MAP[letter]}` : (letter || 'Unknown');
      counts.set(label, (counts.get(label) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([area, count]) => ({ area, count }))
      .sort((a, b) => b.count - a.count);
  }, [gaps]);

  const toggleSort = (col) => {
    setSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  };

  const SortTh = ({ col, label }) => {
    const active = sort.col === col;
    return (
      <th className="sup-sort-head" onClick={() => toggleSort(col)}>
        <span style={{ cursor: 'pointer' }}>
          {label}
          {active && <span className="sup-sort-ico">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
        </span>
      </th>
    );
  };

  const summary = data?.summary;

  // Client-side supplier/area filter on gaps
  const filteredGaps = useMemo(() => {
    let arr = gaps;
    if (supplierFilter) {
      const sf = supplierFilter.toLowerCase();
      arr = arr.filter(g => (g.suppliersCouldFill || []).some(s => s.toLowerCase().includes(sf)));
    }
    if (areaFilter) {
      const af = areaFilter.toLowerCase();
      arr = arr.filter(g => (g.atcCode || '').toLowerCase().startsWith(af.charAt(0)));
    }
    return arr;
  }, [gaps, supplierFilter, areaFilter]);

  const handleExport = useCallback(async (format) => {
    const setLoading = format === 'pptx' ? setExportingPptx : setExportingXlsx;
    setLoading(true); setExportErr('');
    try {
      const filters = {};
      if (regFilter) filters.register = regFilter;
      if (supplierFilter.trim()) filters.supplier = supplierFilter.trim();
      if (areaFilter.trim()) filters.area = areaFilter.trim();
      const meta = await novaGenerateReport(msalInstance, { reportType: 'nl-gap', format, filters, language: reportLang });
      const blob = await novaDownloadReport(msalInstance, meta.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = meta.filename || `nl-gap-report.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setExportErr('Export failed. Please try again.'); }
    finally { setLoading(false); }
  }, [msalInstance, regFilter, supplierFilter, areaFilter, reportLang]);

  return (
    <div className="sup-page">
      <div className="mb-3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>NL market gap analysis</h2>
          <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Analytics</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {exportErr && <span style={{ fontSize: 11, color: 'var(--red)' }}>{exportErr}</span>}
          <ReportLanguageToggle value={reportLang} onChange={setReportLang} compact />
          <button type="button" className="sup-badge teal-soft" disabled={exportingPptx} onClick={() => handleExport('pptx')} style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082',
            cursor: exportingPptx ? 'wait' : 'pointer', background: exportingPptx ? '#e8e8e8' : undefined,
          }}>
            {exportingPptx ? 'Generating...' : 'Download PowerPoint'}
          </button>
          <button type="button" className="sup-badge teal-soft" disabled={exportingXlsx} onClick={() => handleExport('xlsx')} style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082',
            cursor: exportingXlsx ? 'wait' : 'pointer', background: exportingXlsx ? '#e8e8e8' : undefined,
          }}>
            {exportingXlsx ? 'Generating...' : 'Download Excel'}
          </button>
        </div>
      </div>

      <NovaQuickAnalysisBar
        placeholder="Quick NL gap analysis — type a molecule (INN)..."
        knownEntities={(data?.gaps || []).map(g => g.inn)}
        entityLabel="molecule"
        reportType="nl-gap"
        filterBuilder={(entity) => ({ inn: entity })}
        onSubmit={(entity) => setSearch(entity)}
      />

      {/* KPI stat cards */}
      {summary && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            { label: 'Total Gaps', val: summary.totalGaps },
            { label: 'EMA', val: summary.emaGaps },
            { label: 'ANSM', val: summary.ansmGaps },
            { label: 'Unmatched ANSM', val: summary.unmatchedAnsm },
          ].map(s => (
            <div key={s.label} className="sup-kpi" style={{ flex: '1 1 140px', maxWidth: 200 }}>
              <div className="sup-kpi-lbl">{s.label}</div>
              <div className="sup-kpi-val">{s.val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Nova Insight */}
      {data?.novaSummary && (
        <div style={{ ...CHART_CARD, marginBottom: 16 }}>
          <span className="ai-badge" style={{ flexShrink: 0, marginTop: 1 }}>✦ Nova</span>
          <div style={{ fontSize: 13, color: 'var(--navy)', lineHeight: 1.55 }}>{data.novaSummary}</div>
        </div>
      )}

      {/* Gaps by ATC area chart */}
      {!loading && !err && atcChartData.length > 0 && (
        <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e3e8ef', padding: '20px 16px', marginBottom: 16 }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 12 }}>Gaps by Therapeutic Area</div>
          <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            <ResponsiveContainer width="100%" height={Math.min(320, Math.max(160, atcChartData.length * 32 + 30))}>
              <BarChart data={atcChartData} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 10 }}>
                <XAxis type="number" tick={{ fontSize: 12, fill: '#6c757d' }} allowDecimals={false} />
                <YAxis type="category" dataKey="area" width={160} tick={{ fontSize: 11, fill: 'var(--navy)' }} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => [`${v} gap${v !== 1 ? 's' : ''}`, 'Molecules']} contentStyle={{ fontSize: 13, borderRadius: 8 }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={18}>
                  {atcChartData.map((_, i) => (
                    <Cell key={i} fill={GAP_CHART_COLORS[i % GAP_CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="sup-filters-row" style={{ alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {REG_FILTERS.map(f => (
            <button
              key={f.key}
              type="button"
              onClick={() => setRegFilter(f.key)}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                border: regFilter === f.key ? '1px solid var(--teal)' : '1px solid var(--brd)',
                background: regFilter === f.key ? 'var(--teal3)' : 'var(--card)',
                color: regFilter === f.key ? 'var(--teal)' : 'var(--navy)',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div>
          <input
            className="sup-input"
            style={{ width: 180 }}
            placeholder="Search molecule (INN)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <input
            className="sup-input"
            style={{ width: 160 }}
            placeholder="Filter supplier..."
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
          />
        </div>
        <div>
          <input
            className="sup-input"
            style={{ width: 140 }}
            placeholder="ATC area (A, B...)..."
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
          />
        </div>
        {(search || regFilter || supplierFilter || areaFilter) && (
          <button type="button" className="sup-clear-link" onClick={() => { setSearch(''); setRegFilter(''); setSupplierFilter(''); setAreaFilter(''); }}>
            Clear
          </button>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Loading...
        </div>
      )}

      {err && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--red)' }}>{err}</p>
        </div>
      )}

      {!loading && !err && (
        <div className="sup-table-wrap">
          <div className="sup-table-scroll">
            <table className="sup-table">
              <thead>
                <tr>
                  <SortTh col="inn" label="Molecule (INN)" />
                  <th>Foreign Register(s)</th>
                  <SortTh col="atcCode" label="ATC" />
                  <SortTh col="dosageForms" label="Forms" />
                  <SortTh col="suppliersCouldFill" label="Suppliers Who Could Fill" />
                  <th>Available in NL Via</th>
                </tr>
              </thead>
              <tbody>
                {filteredGaps.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: 'var(--t3)' }}>No gaps found.</td></tr>
                ) : (
                  filteredGaps.map((g) => (
                    <tr key={g.inn} role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                      onClick={() => scopeToMolecule(g.inn)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scopeToMolecule(g.inn); } }}
                    >
                      <td className="sup-inn" style={{ fontWeight: 600 }}>{g.inn}</td>
                      <td>
                        {g.foreignRegisters.map(r => (
                          <span key={r} className={`sup-badge ${r === 'EMA' ? 'teal-soft' : 'blue'}`} style={{ marginRight: 4 }}>{r}</span>
                        ))}
                      </td>
                      <td>{g.atcCode || '—'}</td>
                      <td>
                        {(g.dosageForms || []).length === 0 ? '—' : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {g.dosageForms.map((f, i) => (
                              <span key={i} style={{ fontSize: 11, background: '#f3f4f6', borderRadius: 4, padding: '1px 6px' }}>{f}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        {(g.suppliersCouldFill || []).length === 0 ? '—' : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {g.suppliersCouldFill.map((s, i) => (
                              <span key={i} style={{ fontSize: 11, background: 'var(--teal3)', color: 'var(--teal)', borderRadius: 4, padding: '1px 6px', fontWeight: 500 }}>{s}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        {(g.nlAlternatives || []).length === 0 ? <span style={{ color: 'var(--t3)' }}>None</span> : (
                          <div style={{ fontSize: 11.5 }}>
                            {g.nlAlternatives.map((a, i) => (
                              <div key={i}>{a.supplierName}{a.forms.length > 0 ? ` (${a.forms.join(', ')})` : ''}</div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredGaps.length > 0 && (
            <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--t3)', borderTop: '1px solid #eef3f7' }}>
              {filteredGaps.length} gap{filteredGaps.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
